/**
 * OAuth Callback Page
 *
 * Handles the redirect from Azure AD OAuth flow.
 * Extracts tokens from URL hash and stores them, then redirects to the app.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const loaderBg = 'radial-gradient(circle at top left, rgba(37,99,235,0.18), transparent 55%), radial-gradient(circle at 20% 85%, rgba(14,116,144,0.16), transparent 48%), linear-gradient(135deg, #f8fafc 0%, #eef2f7 40%, #f5f7fb 100%)';

export default function AuthCallback(): JSX.Element {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = () => {
      try {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);

        const errorParam = params.get('error');
        if (errorParam) {
          setError(params.get('message') || errorParam);
          return;
        }

        const accessToken = params.get('accessToken');
        const refreshToken = params.get('refreshToken');
        const userJson = params.get('user');

        if (!accessToken || !refreshToken || !userJson) {
          setError('Missing authentication data');
          return;
        }

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        window.history.replaceState(null, '', window.location.pathname);

        navigate('/hub/truck-router/create-transport', { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Failed to process authentication');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div
        className="flex items-center justify-center min-h-screen px-4"
        style={{ background: loaderBg }}
      >
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-slate-200 max-w-md">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-heading mb-2">Authentication Failed</h2>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-2.5 bg-slate-800 text-white font-semibold rounded-xl hover:bg-slate-700 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: loaderBg }}
    >
      <div className="text-center">
        <svg className="animate-spin h-10 w-10 text-slate-800 mx-auto" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-4 text-sm text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
}
